<script setup lang="ts">
import { ref } from 'vue'
import type { EnhancementSourceLoadStatus } from '@/utils/editVehicleEnhancementSource'

defineProps<{
  field: string
  status: EnhancementSourceLoadStatus
  sourceLabel: string
  applicable: boolean
  candidateValue: string
  notice?: string
}>()

const emit = defineEmits<{
  open: []
  retry: []
  use: []
}>()

const menuOpen = ref(false)

function toggleMenu() {
  menuOpen.value = !menuOpen.value
  if (menuOpen.value) emit('open')
}

function useCandidate() {
  emit('use')
  menuOpen.value = false
}
</script>

<template>
  <div class="field-enhancement-source">
    <v-btn
      type="button"
      variant="text"
      size="x-small"
      class="field-enhancement-source__trigger"
      :aria-expanded="menuOpen"
      :data-testid="`edit-vehicle-field-source-${field}`"
      @click="toggleMenu"
    >
      Source
    </v-btn>

    <div
      v-if="menuOpen"
      class="field-enhancement-source__menu"
      role="group"
      :aria-label="`${sourceLabel} source`"
      :data-testid="`edit-vehicle-field-source-menu-${field}`"
    >
      <div v-if="status === 'loading'" class="field-enhancement-source__status">
        {{ sourceLabel }} loading
      </div>

      <template v-else-if="status === 'error'">
        <div class="field-enhancement-source__status">{{ sourceLabel }} could not be loaded.</div>
        <v-btn
          type="button"
          variant="text"
          size="x-small"
          class="field-enhancement-source__action"
          :data-testid="`edit-vehicle-field-source-retry-${field}`"
          @click="emit('retry')"
        >
          Retry
        </v-btn>
      </template>

      <template v-else-if="status === 'loaded' && applicable">
        <div class="field-enhancement-source__label">{{ sourceLabel }}</div>
        <div
          class="field-enhancement-source__value"
          :data-testid="`edit-vehicle-field-source-value-${field}`"
        >
          {{ candidateValue }}
        </div>
        <v-btn
          type="button"
          variant="text"
          size="x-small"
          class="field-enhancement-source__action"
          :data-testid="`edit-vehicle-field-source-use-${field}`"
          @click="useCandidate"
        >
          Use this value
        </v-btn>
      </template>

      <div v-else-if="status === 'loaded'" class="field-enhancement-source__status">
        {{ sourceLabel }} did not return a usable value for this field
      </div>

      <div v-else class="field-enhancement-source__label">{{ sourceLabel }}</div>
    </div>

    <div
      v-if="notice"
      class="field-enhancement-source__notice"
      :data-testid="`edit-vehicle-field-source-notice-${field}`"
    >
      {{ notice }}
    </div>
  </div>
</template>

<style scoped>
.field-enhancement-source {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.field-enhancement-source__trigger {
  min-width: 0;
  height: auto;
  padding-inline: 0;
  letter-spacing: normal;
  text-transform: none;
  font-size: 0.75rem;
  font-weight: 500;
}

.field-enhancement-source__menu {
  min-width: 220px;
  max-width: 320px;
  padding: 8px 10px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 8px;
  background: rgb(var(--v-theme-surface));
}

.field-enhancement-source__label,
.field-enhancement-source__status,
.field-enhancement-source__value,
.field-enhancement-source__notice {
  font-size: 0.75rem;
  line-height: 1.35;
}

.field-enhancement-source__label {
  font-weight: 600;
}

.field-enhancement-source__value {
  margin-top: 2px;
  word-break: break-word;
}

.field-enhancement-source__status,
.field-enhancement-source__notice {
  color: rgba(var(--v-theme-on-surface), 0.7);
}

.field-enhancement-source__action {
  min-width: 0;
  height: auto;
  margin-top: 6px;
  padding-inline: 0;
  letter-spacing: normal;
  text-transform: none;
}
</style>
